// Configuration for each county-scoped civic data section. Keeps the
// list/form pages DRY — the generic CountyChildList and CountyChildForm
// read this map to know URLs, labels, fields, and validation.

export const SECTION_CONFIGS = {
  'election-dates': {
    slug: 'election-dates',
    apiPath: 'election-dates',
    titleSingular: 'Election Date',
    titlePlural: 'Election Dates',
    icon: 'bi-calendar-event',
    nameField: 'election_name',
    namePlaceholder: 'e.g., 2026 Primary',
    listColumns: [
      { key: 'election_name', label: 'Name' },
      { key: 'election_type', label: 'Type' },
      { key: 'election_date', label: 'Date' },
      { key: 'registration_deadline', label: 'Reg. deadline' },
    ],
    formGroups: [
      {
        title: 'Election details',
        fields: [
          { key: 'election_name', label: 'Election name *', required: true, col: 8 },
          { key: 'election_type', label: 'Type', type: 'select', options: ['', 'Primary', 'General', 'Special', 'Municipal', 'Runoff', 'Recall'], col: 4 },
          { key: 'election_date', label: 'Election date', type: 'date', col: 4 },
          { key: 'registration_deadline', label: 'Registration deadline', type: 'date', col: 4 },
          { key: 'vote_by_mail_deadline', label: 'Vote-by-mail deadline', type: 'date', col: 4 },
          { key: 'early_voting_start', label: 'Early voting start', type: 'date', col: 6 },
          { key: 'early_voting_end', label: 'Early voting end', type: 'date', col: 6 },
        ],
      },
    ],
  },

  supervisors: {
    slug: 'supervisors',
    apiPath: 'supervisors',
    titleSingular: 'Supervisor of Elections',
    titlePlural: 'Supervisors of Elections',
    icon: 'bi-person-badge',
    nameField: 'name',
    namePlaceholder: 'e.g., Jane Doe',
    listColumns: [
      { key: 'name', label: 'Name' },
      { key: 'title', label: 'Title' },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
    ],
    formGroups: [
      {
        title: 'Identity',
        fields: [
          { key: 'name', label: 'Name *', required: true, col: 8 },
          { key: 'title', label: 'Title', col: 4 },
          { key: 'photo_url', label: 'Photo URL', type: 'url', col: 12 },
          { key: 'bio', label: 'Bio', type: 'textarea', rows: 4, col: 12 },
        ],
      },
      {
        title: 'Contact & hours',
        fields: [
          { key: 'phone', label: 'Phone', col: 4 },
          { key: 'email', label: 'Email', type: 'email', col: 4 },
          { key: 'website', label: 'Website', type: 'url', col: 4 },
          { key: 'office_address', label: 'Office address', type: 'textarea', rows: 2, col: 6 },
          { key: 'mailing_address', label: 'Mailing address', type: 'textarea', rows: 2, col: 6 },
          { key: 'hours', label: 'Hours of operation', type: 'textarea', rows: 2, col: 12 },
        ],
      },
    ],
  },

  'party-organizations': {
    slug: 'party-organizations',
    apiPath: 'party-organizations',
    titleSingular: 'Party Organization',
    titlePlural: 'Party Organizations',
    icon: 'bi-flag',
    nameField: 'organization_name',
    namePlaceholder: 'e.g., Miami-Dade Democratic Party',
    listColumns: [
      { key: 'organization_name', label: 'Organization' },
      { key: 'party', label: 'Party' },
      { key: 'chair_name', label: 'Chair' },
      { key: 'website', label: 'Website' },
    ],
    formGroups: [
      {
        title: 'Organization',
        fields: [
          { key: 'organization_name', label: 'Organization name *', required: true, col: 8 },
          { key: 'party', label: 'Party *', type: 'select', options: ['', 'Democratic', 'Republican', 'Independent', 'Libertarian', 'Green', 'Other'], required: true, col: 4 },
          { key: 'chair_name', label: 'Chair name', col: 6 },
          { key: 'website', label: 'Website', type: 'url', col: 6 },
        ],
      },
      {
        title: 'Contact & meetings',
        fields: [
          { key: 'email', label: 'Email', type: 'email', col: 6 },
          { key: 'phone', label: 'Phone', col: 6 },
          { key: 'address', label: 'Address', type: 'textarea', rows: 2, col: 12 },
          { key: 'meeting_info', label: 'Meeting info', type: 'textarea', rows: 3, col: 12 },
        ],
      },
    ],
  },

  'local-groups': {
    slug: 'local-groups',
    apiPath: 'local-groups',
    titleSingular: 'Local Group',
    titlePlural: 'Local Groups',
    icon: 'bi-people-fill',
    nameField: 'name',
    namePlaceholder: 'e.g., League of Women Voters',
    listColumns: [
      { key: 'name', label: 'Name' },
      { key: 'group_type', label: 'Type' },
      { key: 'website', label: 'Website' },
      { key: 'phone', label: 'Phone' },
    ],
    formGroups: [
      {
        title: 'Group',
        fields: [
          { key: 'name', label: 'Name *', required: true, col: 8 },
          { key: 'group_type', label: 'Group type', type: 'select', options: ['', 'Civic', 'Advocacy', 'Voter Outreach', 'Community', 'Religious', 'Educational', 'Other'], col: 4 },
          { key: 'logo_url', label: 'Logo URL', type: 'url', col: 6 },
          { key: 'website', label: 'Website', type: 'url', col: 6 },
          { key: 'description', label: 'Description', type: 'textarea', rows: 4, col: 12 },
        ],
      },
      {
        title: 'Contact',
        fields: [
          { key: 'email', label: 'Email', type: 'email', col: 6 },
          { key: 'phone', label: 'Phone', col: 6 },
          { key: 'address', label: 'Address', type: 'textarea', rows: 2, col: 12 },
        ],
      },
    ],
  },
};

export const SECTION_ORDER = ['election-dates', 'supervisors', 'party-organizations', 'local-groups'];

export function getSectionConfig(slug) {
  return SECTION_CONFIGS[slug];
}

export const DATA_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];
